/* dev-impersonate.js — returning-user test harness + Fake Customer Engine driver.
 *
 * Lets an operator load the storefront AS a known buyer to exercise the
 * returning-user / new-user urgency flows, by injecting a visitor_id into
 * localStorage BEFORE the engine + purchase-info.js boot (this script is wired
 * to load ahead of them).
 *
 *   ?__se_as=<visitorId>            on the URL → adopt that buyer for this +
 *                                   subsequent page loads in this browser.
 *   ?__se_as=clear                  → drop the impersonated identity.
 *   window.SE_Dev.asBuyer('<vid>')  → set + reload from the console.
 *   window.SE_Dev.asBuyer('clear')  → clear + reload.
 *   window.SE_Dev.resetFake('<vid>')→ scoped clean-slate + re-impersonate a fake.
 *
 * GATE — two tiers:
 *   • PREVIEW themes (Shopify.theme.role !== 'main', CLI dev, *.shopifypreview):
 *     full harness — impersonate ANY visitor_id, full storage wipes.
 *   • LIVE published theme (role === 'main'): impersonation is allowed ONLY for
 *     a FAKE Customer Engine id (facade00-…). A real shopper can never be made
 *     to impersonate another buyer, and a real id is rejected on live. This is
 *     what lets the operator drive a synthetic customer through the REAL live
 *     flows — the db-app short-circuits every fake id to its panel profile, so
 *     nothing the fake does touches real data. Pairs with the panel's Fake
 *     Customer Engine ("Impersonate" builds the ?__se_as=facade00-… link).
 */
(function () {
  'use strict';

  var UUID_RE = /^[a-fA-F0-9-]{32,40}$/;

  // Keep IN SYNC with db-app panel_config.FAKE_VISITOR_PREFIX and panel-back
  // fake_customers.FAKE_VISITOR_PREFIX. All-hex so it passes UUID_RE above.
  var FAKE_PREFIX = 'facade00-';
  function isFakeId(vid) {
    return typeof vid === 'string' && vid.indexOf(FAKE_PREFIX) === 0;
  }

  // The visitorID COOKIE is the authoritative identity on this theme:
  // recommendations.js boots cookie-first and (a) syncs a differing cookie
  // straight over localStorage, (b) with NO cookie at all MINTS a fresh id +
  // 300-day cookie over whatever localStorage held. So an adopt() that only
  // wrote localStorage was silently undone one script later (observed on live
  // 2026-08-24: adopted facade clobbered by a freshly-minted id in the same
  // page load). Adoption therefore owns the cookie too. The operator's own
  // cookie is snapshotted first (tab-scoped; adopt() runs AFTER our own wipes
  // so the snapshot survives them) and clear puts it back byte-for-byte.
  var PREV_COOKIE_KEY = 'SE_PrevVisitorCookie';   // '' means "there was none"
  function _readVidCookie() {
    try {
      var m = document.cookie.match(/(?:^|; )visitorID=([^;]*)/);
      return m ? m[1] : null;                     // RAW value, not decoded
    } catch (e) { return null; }
  }
  function _setVidCookie(rawVal, days) {
    // days=null gives a session cookie (dies with the browser; right for a fake).
    try {
      var exp = days ? '; expires=' + new Date(Date.now() + days * 864e5).toUTCString() : '';
      document.cookie = 'visitorID=' + rawVal + exp + '; path=/';
    } catch (e) {}
  }
  function _deleteVidCookie() {
    try { document.cookie = 'visitorID=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'; } catch (e) {}
  }

  // Exposed so the analytics beacons (CAE / TTB / gear-builder) can DROP events
  // for a synthetic visitor — the Fake Customer Engine "drop events entirely"
  // rule. dev-impersonate loads before every beacon, so this is always defined
  // by the time they fire. Reads the CURRENTLY adopted id each call.
  // DO NOT CLOBBER session-guard.js. That file loads EARLIER (both are `defer`,
  // so document order wins) and exports a strictly better version — one that
  // also catches an "assumed" session, which this prefix check cannot express.
  // Overwriting it here would silently reopen the analytics leak while browsing
  // as a real customer. Only define it when the guard is absent, so an old
  // theme build (or a failed guard load) still behaves exactly as before.
  try {
    if (typeof window.SE_isInternalSession !== 'function') {
      window.SE_isInternalSession = function () {
        try { return isFakeId(localStorage.getItem('visitorID') || ''); } catch (e) { return false; }
      };
    }
  } catch (e) {}

  // Only treat as "preview" off the live published theme. Prefer Shopify's own
  // signal; fall back to non-live hostnames so it still works on CLI dev /
  // *.shopifypreview.
  function isPreview() {
    try {
      var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
      if (role) return role !== 'main';
    } catch (e) {}
    try {
      return /(\.myshopify\.com|\.shopifypreview\.com)$|^(127\.|localhost)/.test(location.hostname || '');
    } catch (e) { return false; }
  }

  // Adopting a given id is allowed on preview for anyone, but on LIVE only for a
  // fake (facade00-) id.
  function canImpersonate(vid) {
    return isPreview() || isFakeId(vid);
  }

  // Keys keyed to the previous identity that must be cleared so the new buyer's
  // purchase signal + recs + country are fetched fresh (see purchase-info.js /
  // recommendations.js).
  var IDENTITY_CACHE_KEYS = [
    'SE_purchaseInfo',
    'recommendedProducts',
    'recommendationFetchVisitorId',
    'recommendationFetchTime',
    'country'
  ];

  function adopt(vid) {
    // TELL THE GUARD FIRST. This is the whole reason session-guard.js exists.
    //
    // Adopting a REAL customer's id makes this browser byte-identical to that
    // customer: the id is theirs, so nothing downstream — client or server —
    // can tell the difference by looking. Without this call the guard resolves
    // kind() to 'shopper' and every write is permitted, which is exactly the
    // hole the guard was built to close. begin() is what turns inference into
    // an explicit, tab-scoped declaration.
    //
    // A facade00- id is deliberately NOT marked 'assumed': there is no real
    // person behind it, its prefix already resolves to 'internal', and the
    // synthetic-visitor workflows depend on analytics still flowing.
    // begin() BEFORE the writes below, so the snapshot captures the operator's
    // own identity rather than the one we are about to overwrite.
    try {
      if (window.SE_Session && !isFakeId(vid)) window.SE_Session.begin('assumed', { ref: null });
    } catch (e) {}

    try {
      // Snapshot the operator's own cookie ONCE per tab (adopting a second
      // buyer must not overwrite the original snapshot with the first fake).
      if (sessionStorage.getItem(PREV_COOKIE_KEY) === null) {
        var prevC = _readVidCookie();
        sessionStorage.setItem(PREV_COOKIE_KEY, prevC === null ? '' : prevC);
      }
    } catch (e) {}
    _setVidCookie(vid, null);   // session cookie: cookie-first readers now agree

    try {
      localStorage.setItem('viewerId', vid);
      localStorage.setItem('visitorID', vid);
      IDENTITY_CACHE_KEYS.forEach(function (k) { localStorage.removeItem(k); });
      try { sessionStorage.removeItem('recommendationFetchFailed'); } catch (e) {}
    } catch (e) {}
  }

  function clearAdopted() {
    try {
      ['viewerId', 'visitorID'].concat(IDENTITY_CACHE_KEYS).forEach(function (k) {
        localStorage.removeItem(k);
      });
    } catch (e) {}

    // Put the operator's own cookie back exactly as it was, or remove ours if
    // they never had one. Without this the fake's cookie outlives the exit and
    // cookie-first readers resurrect the fake on the next load ("clear" that
    // does not clear). Restored at 300 days to match how recommendations.js
    // mints (that is what the original cookie was).
    try {
      var prev = sessionStorage.getItem(PREV_COOKIE_KEY);
      sessionStorage.removeItem(PREV_COOKIE_KEY);
      if (prev) { _setVidCookie(prev, 300); localStorage.setItem('visitorID', prev); }
      else _deleteVidCookie();
    } catch (e) {}

    // Tear the marker down too, or the tab stays 'assumed' after the identity
    // it described is gone — every write would keep being blocked with nothing
    // left to protect, and the operator would have no way to tell why.
    // end() is not used here: it does its own storage clear + reload, and this
    // function is called mid-flow by callers that reload themselves.
    try {
      if (window.SE_Session && window.SE_Session.isAssumed && window.SE_Session.isAssumed()) {
        sessionStorage.removeItem('AGB_SessionState');
      }
    } catch (e) {}
  }

  // Drop a one-shot marker the scarcity engine reads on the POST-reload page to
  // emit its verbose "IMPERSONATE …" summary toast (who am I now / flow /
  // delivered / owned). FAKE ids only — the engine only summarizes fakes. MUST
  // be called AFTER any _scopedWipe()/_cleanSlate() (those sessionStorage.clear()
  // and would wipe this marker). The engine reads + removes it (one-shot).
  function announceImpersonate(vid, kind) {
    try {
      if (isFakeId(vid)) {
        sessionStorage.setItem('SE_ImpersonateAnnounce', kind === 'reset' ? 'reset' : 'adopt');
      }
    } catch (e) {}
  }

  // Full wipe — PREVIEW only. Nukes ALL storage so no leftover identity / engine
  // scarcity state / once-per-session gates bleed between tests.
  function _wipe() {
    try { localStorage.clear(); } catch (e) {}
    try { sessionStorage.clear(); } catch (e) {}
  }

  // Scoped wipe — safe on LIVE. Clears only the keys our personalization stack
  // owns (identity, recs, country, scarcity engine, TTB flags) so a fake test
  // starts clean WITHOUT nuking unrelated storage. sessionStorage is per-tab so
  // it's safe to clear outright (re-arms session gates).
  function _scopedWipe() {
    try {
      var kill = [];
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        if (k && /^(AGB_|SE_|TTB_|ttb_|recommend|viewerId|visitorID|country)/i.test(k)) kill.push(k);
      }
      kill.forEach(function (k) { try { localStorage.removeItem(k); } catch (e) {} });
    } catch (e) {}
    try { sessionStorage.clear(); } catch (e) {}
  }

  // Preview → full wipe; live → scoped wipe.
  function _cleanSlate() { if (isPreview()) _wipe(); else _scopedWipe(); }

  function _warnLiveRealId(vid) {
    try { console.warn('[dev-impersonate] live impersonation is limited to fake (facade00-) ids — refused:', vid); } catch (e) {}
  }

  // ── Console helpers ──────────────────────────────────────────────────
  window.SE_Dev = window.SE_Dev || {};

  // asBuyer('<vid>') — adopt + reload. 'clear' drops the adopted identity.
  window.SE_Dev.asBuyer = function (vid) {
    if (vid === 'clear') { clearAdopted(); location.reload(); return 'cleared — reloading'; }
    if (!UUID_RE.test(String(vid || ''))) {
      try { console.warn('[dev-impersonate] not a visitor_id UUID:', vid); } catch (e) {}
      return false;
    }
    if (!canImpersonate(vid)) { _warnLiveRealId(vid); return false; }
    adopt(String(vid));
    announceImpersonate(vid, 'adopt');
    location.reload();
    return 'reloading as ' + vid;
  };

  // testAs('<vid>') — clean slate + impersonate + reload. Real ids: preview only.
  window.SE_Dev.testAs = function (vid) {
    if (!UUID_RE.test(String(vid || ''))) {
      try { console.warn('[dev-impersonate] testAs needs a visitor_id UUID:', vid); } catch (e) {}
      return false;
    }
    if (!canImpersonate(vid)) { _warnLiveRealId(vid); return false; }
    _cleanSlate(); adopt(String(vid)); announceImpersonate(vid, 'adopt'); location.reload();
    return 'clean test as ' + vid + ' — reloading';
  };

  // resetFake('<vid>') — Fake Customer Engine reset: scoped clean-slate + re-adopt
  // the fake + reload. Works on LIVE (fake-only). The whole point of the "Reset"
  // button in the panel: re-arm the fake's workflows (low-stock lifecycles, the
  // returning reaper, once-per-shopper TTB) without disturbing real storage.
  // Defaults to the currently-adopted id when called with no arg.
  window.SE_Dev.resetFake = function (vid) {
    vid = String(vid || localStorage.getItem('visitorID') || '');
    if (!isFakeId(vid)) {
      try { console.warn('[dev-impersonate] resetFake needs a fake (facade00-) id, got:', vid); } catch (e) {}
      return false;
    }
    _scopedWipe(); adopt(vid); announceImpersonate(vid, 'reset'); location.reload();
    return 'reset fake ' + vid + ' — reloading clean';
  };

  // testNew() — clean slate, no identity → exercises the new-visitor path. Full
  // wipe, so PREVIEW only.
  window.SE_Dev.testNew = function () {
    if (!isPreview()) { try { console.warn('[dev-impersonate] testNew is preview-only (full wipe)'); } catch (e) {} return false; }
    _wipe(); location.reload();
    return 'clean new-visitor test — reloading';
  };

  // reset() — full wipe + reload (no scenario). PREVIEW only; on live use resetFake.
  window.SE_Dev.reset = function () {
    if (!isPreview()) { try { console.warn('[dev-impersonate] reset is preview-only (full wipe) — use SE_Dev.resetFake(<facade00-id>) on live'); } catch (e) {} return false; }
    _wipe(); location.reload();
    return 'reset — reloading clean';
  };

  // status() — read-only one-glance readout of what the system DECIDED: flow,
  // resolved identity, purchase summary, and the products currently set to
  // low-stock with their live counts. Safe everywhere (no writes).
  window.SE_Dev.status = function () {
    var s = {};
    try {
      var eng = window.SE_Engine || null;
      s.flow = window.__userFlow || (eng && eng._activeFlow) || '(unresolved)';
      s.identity = window.SE_visitorIdentity || '(not resolved yet)';
      s.fake = isFakeId(localStorage.getItem('visitorID') || '');
      var pi = window.SE_purchaseInfo;
      s.purchase = pi
        ? { known: pi.known, has_purchased: pi.has_purchased, delivered: pi.delivered, order_count: pi.order_count, owned_skus: (pi.purchased_products || []).length }
        : '(no purchase info yet)';
      var low = [], seen = {};
      var lc = (eng && eng.state && eng.state.lifecycles) || {};
      for (var key in lc) {
        if (!Object.prototype.hasOwnProperty.call(lc, key)) continue;
        var handle = key.split('_')[0];
        if (!handle || seen[handle]) continue;
        seen[handle] = true;
        var c = null;
        try { var sd = eng.getStockData(handle, key.slice(handle.length + 1)); c = sd && sd.count; } catch (e) {}
        low.push(handle + (c != null ? ' (' + c + ' left)' : ''));
      }
      s.low_stock = low.length ? low : '(none)';
    } catch (e) { s.error = String(e); }
    try { console.log('%c[SE_Dev.status]', 'font-weight:bold;color:#c50', s); } catch (e) {}
    return s;
  };

  // ── URL-driven: ?__se_as=<vid> (or =clear), optional &__se_reset=1 ─────
  // We load BEFORE the engine + purchase-info.js, so setting localStorage now is
  // enough — no reload needed. Strip the params afterwards so they don't linger /
  // re-fire. On live, a non-fake id is refused (canImpersonate gate).
  //   __se_reset=1 → scoped clean-slate for the FAKE first (delete its browser-
  //   side scarcity status: low-stock lifecycles, returning reaper, once-per-
  //   shopper TTB, cached recs/country). Pairs with the panel's "Reset & open"
  //   button. Reset NEVER touches a non-fake id.
  try {
    var qs = new URLSearchParams(location.search);
    var wantReset = qs.has('__se_reset');
    var touched = false;
    if (qs.has('__se_as')) {
      var as = qs.get('__se_as');
      if (as === 'clear') { clearAdopted(); touched = true; }
      else if (UUID_RE.test(as) && canImpersonate(as)) {
        if (wantReset && isFakeId(as)) _scopedWipe();   // clears visitorID too…
        adopt(as);                                       // …then re-adopt it.
        announceImpersonate(as, (wantReset && isFakeId(as)) ? 'reset' : 'adopt');
        touched = true;
        try { console.log('[dev-impersonate] adopted buyer:', as, isFakeId(as) ? '(fake)' : '(preview)', (wantReset && isFakeId(as)) ? '+ reset' : ''); } catch (e) {}
      }
      else if (UUID_RE.test(as)) { _warnLiveRealId(as); }
    } else if (wantReset) {
      // Reset the CURRENTLY-adopted fake (no new id supplied).
      var curVid = '';
      try { curVid = localStorage.getItem('visitorID') || ''; } catch (e) {}
      if (isFakeId(curVid)) {
        _scopedWipe(); adopt(curVid); announceImpersonate(curVid, 'reset'); touched = true;
        try { console.log('[dev-impersonate] reset fake:', curVid); } catch (e) {}
      }
    }
    if (touched || wantReset) {
      qs.delete('__se_as');
      qs.delete('__se_reset');
      var clean = location.pathname + (qs.toString() ? '?' + qs.toString() : '') + location.hash;
      try { history.replaceState(null, '', clean); } catch (e) {}
    }
  } catch (e) {}
})();
